import { useState, useCallback, useRef } from 'react'
import { useMedplum } from '@medplum/react'
import { AuditEvent, Bot } from '@medplum/fhirtypes'
import { logError } from '@/utils/errorUtils'

type SummaryState = {
  summary: string | null
  loading: boolean
  error: string | null
}

type ClinicalItem = {
  resourceType: string
  title: string
  recorded: string
}

/**
 * Hook for fetching and caching AI-generated patient summaries
 *
 * Usage:
 * const { fetchSummary, getState } = usePatientSummary()
 *
 * // On patient group expand:
 * const key = `${date}|${patientId}`
 * if (!getState(key).summary) {
 *   fetchSummary(key, patientName, patientInfo, date, items)
 * }
 *
 * // Get state:
 * const { summary, loading, error } = getState(key)
 */
export function usePatientSummary() {
  const medplum = useMedplum()
  const [cache, setCache] = useState<Map<string, string>>(new Map())
  const [states, setStates] = useState<Map<string, SummaryState>>(new Map())
  const botRef = useRef<Bot | null>(null)

  const fetchSummary = useCallback(
    async (
      key: string, // "date|patientId"
      patientName: string,
      patientInfo: string,
      date: string,
      items: AuditEvent[],
    ): Promise<string | null> => {
      // Check cache first
      if (cache.has(key)) {
        return cache.get(key) || null
      }

      // Check if already loading
      const currentState = states.get(key)
      if (currentState?.loading) {
        return null
      }

      // Set loading state
      setStates((prev) => {
        const next = new Map(prev)
        next.set(key, { summary: null, loading: true, error: null })
        return next
      })

      try {
        // Find or cache the bot reference
        if (!botRef.current) {
          const bot = await medplum.searchOne('Bot', 'name=generate-patient-summary')
          if (!bot) {
            throw new Error('Summary bot not found. Please deploy generate-patient-summary bot.')
          }
          botRef.current = bot
        }

        // Extract clinical items from audit events
        const clinicalItems = extractClinicalItems(items)

        // Execute bot
        const result = await medplum.executeBot(botRef.current.id as string, {
          resourceType: 'Parameters',
          parameter: [
            { name: 'patientName', valueString: patientName },
            { name: 'patientInfo', valueString: patientInfo },
            { name: 'date', valueString: date },
            { name: 'items', valueString: JSON.stringify(clinicalItems) },
          ],
        })

        // Extract summary from result
        const summary =
          typeof result === 'string'
            ? result
            : (result as { summary?: string })?.summary || 'Summary unavailable.'

        // Update cache and state
        setCache((prev) => {
          const next = new Map(prev)
          next.set(key, summary)
          return next
        })

        setStates((prev) => {
          const next = new Map(prev)
          next.set(key, { summary, loading: false, error: null })
          return next
        })

        return summary
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to generate summary'
        logError('patient', 'Summary generation error', err)

        setStates((prev) => {
          const next = new Map(prev)
          next.set(key, { summary: null, loading: false, error: errorMessage })
          return next
        })

        return null
      }
    },
    [medplum, cache, states],
  )

  const getState = useCallback(
    (key: string): SummaryState => {
      const state = states.get(key)
      if (state) return state

      // If we have a cached value but no state, return it
      const cachedSummary = cache.get(key)
      if (cachedSummary) {
        return { summary: cachedSummary, loading: false, error: null }
      }

      return { summary: null, loading: false, error: null }
    },
    [states, cache],
  )

  return { fetchSummary, getState }
}

/**
 * Extract clinical summary data from AuditEvent items
 */
type FhirResource = {
  resourceType?: string
  code?: { text?: string; coding?: Array<{ display?: string }> }
  type?: Array<{ text?: string; coding?: Array<{ display?: string }> }>
  medicationCodeableConcept?: { text?: string }
  vaccineCode?: { text?: string; coding?: Array<{ display?: string }> }
}

function extractClinicalItems(auditEvents: AuditEvent[]): ClinicalItem[] {
  return auditEvents.map((event) => {
    const resource = event.entity?.[0]?.what?.resource as FhirResource | undefined
    const resourceType = resource?.resourceType || 'Unknown'

    let title = ''
    if (resource) {
      switch (resourceType) {
        case 'Observation':
          title = resource.code?.text || resource.code?.coding?.[0]?.display || 'Observation'
          break
        case 'DiagnosticReport':
          title = resource.code?.text || resource.code?.coding?.[0]?.display || 'Report'
          break
        case 'Encounter':
          title =
            resource.type?.[0]?.text || resource.type?.[0]?.coding?.[0]?.display || 'Encounter'
          break
        case 'MedicationRequest':
          title = resource.medicationCodeableConcept?.text || 'Medication'
          break
        case 'Condition':
          title = resource.code?.text || resource.code?.coding?.[0]?.display || 'Condition'
          break
        case 'Procedure':
          title = resource.code?.text || resource.code?.coding?.[0]?.display || 'Procedure'
          break
        case 'Immunization':
          title =
            resource.vaccineCode?.text ||
            resource.vaccineCode?.coding?.[0]?.display ||
            'Immunization'
          break
        case 'AllergyIntolerance':
          title = resource.code?.text || resource.code?.coding?.[0]?.display || 'Allergy'
          break
        default:
          title = resource.code?.text || resourceType
      }
    }

    return {
      resourceType,
      title,
      recorded: event.recorded || '',
    }
  })
}
